import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, X } from "lucide-react";
import api from "../../lib/api";
import { useConfirm } from "../../components/ConfirmDialog";

const CATEGORIES = [
    { id: "pitch", label: "Pitch", color: "#EE722E" },
    { id: "objection", label: "Objection", color: "#FCDB53" },
    { id: "closing", label: "Closing", color: "#F89F63" },
];

function CardForm({ initial, onSave, onCancel }) {
    const [form, setForm] = useState(
        initial || { category: "pitch", title: "", content: "", tags: [] },
    );
    const [tagInput, setTagInput] = useState((initial?.tags || []).join(", "));

    const submit = () => {
        if (!form.title.trim() || !form.content.trim()) {
            toast.error("Titre et contenu requis");
            return;
        }
        const tags = tagInput
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean);
        onSave({ ...form, tags });
    };

    return (
        <div className="glass-card rounded-3xl p-5 space-y-3" data-testid="terrain-card-form">
            <div className="flex gap-2 flex-wrap">
                {CATEGORIES.map((c) => (
                    <button
                        key={c.id}
                        type="button"
                        onClick={() => setForm({ ...form, category: c.id })}
                        data-testid={`terrain-form-cat-${c.id}`}
                        className={`px-4 py-2 rounded-2xl text-xs font-bold transition-all ${
                            form.category === c.id
                                ? "bg-shariti-orange text-white"
                                : "bg-white/5 text-white/60 hover:text-white"
                        }`}
                    >
                        {c.label}
                    </button>
                ))}
            </div>
            <input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Titre"
                data-testid="terrain-form-title"
                className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder-white/30 outline-none focus:border-shariti-orange/60"
            />
            <textarea
                rows={4}
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                placeholder="Contenu (le pitch / la réponse à l'objection / etc.)"
                data-testid="terrain-form-content"
                className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder-white/30 outline-none focus:border-shariti-orange/60 resize-none"
            />
            <input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                placeholder="tags séparés par des virgules"
                data-testid="terrain-form-tags"
                className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder-white/30 outline-none focus:border-shariti-orange/60"
            />
            <div className="flex gap-2">
                <button
                    onClick={submit}
                    data-testid="terrain-form-submit"
                    className="flex-1 bg-shariti-orange text-white font-extrabold py-3 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all"
                >
                    {initial ? "Enregistrer" : "Créer"}
                </button>
                <button
                    onClick={onCancel}
                    className="px-4 py-3 rounded-2xl bg-white/5 hover:bg-white/10 text-white/70"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}

export default function AdminTerrain() {
    const confirm = useConfirm();
    const [cards, setCards] = useState([]);
    const [creating, setCreating] = useState(false);
    const [editing, setEditing] = useState(null);
    const [filter, setFilter] = useState("all");

    const reload = async () => {
        const r = await api.get("/terrain");
        setCards(r.data);
    };

    useEffect(() => {
        reload();
    }, []);

    const create = async (data) => {
        try {
            await api.post("/admin/terrain", data);
            toast.success("Carte créée");
            setCreating(false);
            reload();
        } catch {
            toast.error("Erreur");
        }
    };

    const update = async (data) => {
        try {
            await api.patch(`/admin/terrain/${editing.card_id}`, data);
            toast.success("Carte mise à jour");
            setEditing(null);
            reload();
        } catch {
            toast.error("Erreur");
        }
    };

    const remove = async (card_id) => {
        const target = cards.find((c) => c.card_id === card_id);
        const ok = await confirm({
            title: "Supprimer cette carte ?",
            description: target
                ? `« ${target.title} » sera supprimée du Terrain Rapide. Cette action est irréversible.`
                : "Cette action est irréversible.",
            confirmLabel: "Supprimer",
            tone: "danger",
        });
        if (!ok) return;
        try {
            await api.delete(`/admin/terrain/${card_id}`);
            toast.success("Carte supprimée");
            reload();
        } catch {
            toast.error("Suppression impossible");
        }
    };

    const filtered = filter === "all" ? cards : cards.filter((c) => c.category === filter);

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <p className="text-xs font-thin tracking-[0.25em] uppercase text-white/50 mb-1">
                        Mode terrain rapide
                    </p>
                    <h2 className="text-2xl font-extrabold text-white">
                        {cards.length} carte{cards.length > 1 ? "s" : ""}
                    </h2>
                </div>
                <button
                    onClick={() => {
                        setCreating(true);
                        setEditing(null);
                    }}
                    data-testid="admin-new-terrain-button"
                    className="inline-flex items-center gap-2 bg-shariti-orange text-white font-extrabold py-2.5 px-4 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all"
                >
                    <Plus className="w-4 h-4" />
                    Nouvelle carte
                </button>
            </div>

            <div className="flex gap-2 p-1.5 glass-card rounded-full w-fit">
                <button
                    onClick={() => setFilter("all")}
                    className={`px-4 py-1.5 rounded-full text-sm font-bold ${
                        filter === "all" ? "bg-shariti-orange text-white" : "text-white/60"
                    }`}
                >
                    Tout
                </button>
                {CATEGORIES.map((c) => (
                    <button
                        key={c.id}
                        onClick={() => setFilter(c.id)}
                        data-testid={`admin-terrain-filter-${c.id}`}
                        className={`px-4 py-1.5 rounded-full text-sm font-bold capitalize ${
                            filter === c.id
                                ? "bg-shariti-orange text-white"
                                : "text-white/60"
                        }`}
                    >
                        {c.label}
                    </button>
                ))}
            </div>

            {creating && (
                <CardForm onSave={create} onCancel={() => setCreating(false)} />
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {filtered.map((card) => {
                    const isEditing = editing?.card_id === card.card_id;
                    if (isEditing) {
                        return (
                            <CardForm
                                key={card.card_id}
                                initial={card}
                                onSave={update}
                                onCancel={() => setEditing(null)}
                            />
                        );
                    }
                    const cat = CATEGORIES.find((c) => c.id === card.category);
                    return (
                        <motion.div
                            key={card.card_id}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="glass-card rounded-3xl p-5"
                            data-testid={`admin-terrain-card-${card.card_id}`}
                        >
                            <span
                                className="text-[10px] font-extrabold uppercase tracking-widest px-2 py-1 rounded-full inline-block mb-3"
                                style={{ background: cat?.color, color: "#181818" }}
                            >
                                {card.category}
                            </span>
                            <h3 className="text-lg font-extrabold text-white tracking-tight mb-2">
                                {card.title}
                            </h3>
                            <p className="text-sm text-white/60 font-light line-clamp-3 mb-4">
                                {card.content}
                            </p>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => {
                                        setEditing(card);
                                        setCreating(false);
                                    }}
                                    data-testid={`admin-terrain-edit-${card.card_id}`}
                                    className="flex-1 inline-flex items-center justify-center gap-1.5 bg-shariti-orange/20 text-shariti-orange-light hover:bg-shariti-orange hover:text-white py-2 rounded-xl text-xs font-bold transition-all"
                                >
                                    <Edit3 className="w-3.5 h-3.5" />
                                    Modifier
                                </button>
                                <button
                                    onClick={() => remove(card.card_id)}
                                    data-testid={`admin-terrain-delete-${card.card_id}`}
                                    className="px-3 py-2 rounded-xl bg-white/5 hover:bg-red-500/20 transition-colors"
                                >
                                    <Trash2 className="w-3.5 h-3.5 text-white/70" />
                                </button>
                            </div>
                        </motion.div>
                    );
                })}
            </div>
        </div>
    );
}
